import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import Loading from './Loading';
import ErrorState from './ErrorState';

/**
 * <DataTable /> — Jobs / Billing / Staff / Files tables.
 *
 * columns: [{ key, label, align?, width?, render?(row,i), thStyle?, tdStyle?, sortable?, sortValue?(row),
 *             primary?, mobileHide? }]
 * rows:    array of row objects
 * getRowKey(row,i)        stable key (strongly recommended — selection is keyed by it)
 * getRowLabel(row,i)      accessible name for the row ("JOB-0012 Facade anchor test");
 *                         defaults to the primary column's plain value
 * onRowClick(row,i)       makes rows openable by mouse, Enter and Space
 * minWidth                px for the desktop horizontal-scroll wrapper
 *
 * States (checked in this order):
 *   error + onRetry       → <ErrorState thing={errorThing}> instead of rows
 *   loading               → skeleton rows (only when there are no rows yet)
 *   rows.length === 0     → emptyState (node) or emptyText
 *
 * Selection (selectable):
 *   Controlled:   selectedKeys (Set) + onSelectionChange(nextSet)        ← preferred
 *   Legacy:       selectedKeys + onToggleRow(key,row) + onToggleAll(visibleKeys, allSelected)
 *   Uncontrolled: omit selectedKeys; read changes via onSelectionChange
 *   When `rows` change, keys that are no longer present are pruned and
 *   onSelectionChange fires — bulk actions can never hit rows you can't see.
 *
 * Sorting: columns with a non-empty label are sortable (none → asc → desc → none);
 * opt out with sortable: false. Sort key is sortValue(row) if given, else row[col.key].
 *
 * MOBILE (≤768px): one card per row. The `primary` column (or the first) is the
 * heading; `mobileHide` drops columns. An invisible full-card button opens the
 * row, so cards are tappable without nesting interactive controls.
 */
const isEmptyValue = (v) => v === null || v === undefined || v === '' || v === '—' || v === '-';

const compareValues = (a, b) => {
  if (isEmptyValue(a) && isEmptyValue(b)) return 0;
  if (isEmptyValue(a)) return 1;
  if (isEmptyValue(b)) return -1;
  const as = String(a).replace(/[£,\s]/g, '').replace('−', '-');
  const bs = String(b).replace(/[£,\s]/g, '').replace('−', '-');
  const an = Number(as);
  const bn = Number(bs);
  if (as !== '' && bs !== '' && Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  return String(a).toLowerCase().localeCompare(String(b).toLowerCase());
};

/** True while the viewport is phone-sized. */
export const useIsNarrow = (px = 768) => {
  const q = `(max-width: ${px}px)`;
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : false);
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(q);
    const on = (e) => setNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    setNarrow(mq.matches);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, [q]);
  return narrow;
};

/** Clicks that land on a control inside a row belong to that control, not the row. */
const INTERACTIVE = 'a, button, input, select, textarea, label, summary, [role="button"], [role="checkbox"], [role="switch"], [role="menuitem"], [contenteditable="true"]';
const fromInteractive = (e) => {
  const hit = e.target && e.target.closest ? e.target.closest(INTERACTIVE) : null;
  return !!hit && hit !== e.currentTarget && e.currentTarget.contains(hit);
};

const plain = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

const sameSet = (a, b) => a.size === b.size && [...a].every((k) => b.has(k));

const DataTable = ({
  columns = [],
  rows = [],
  onRowClick,
  getRowKey,
  getRowLabel,
  minWidth = 1000,
  selectable = false,
  selectedKeys: selectedProp,
  onSelectionChange,
  onToggleRow,
  onToggleAll,
  emptyText = 'Nothing to show yet.',
  emptyState,
  loading = false,
  error = null,
  onRetry,
  errorThing = 'this list',
  caption,
}) => {
  const openHintId = `cw-dt-${useId().replace(/:/g, '')}-open`;
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [ownSelection, setOwnSelection] = useState(() => new Set());
  const narrow = useIsNarrow();
  const rowKeyOf = (row, i) => (getRowKey ? getRowKey(row, i) : i);
  const controlled = selectedProp instanceof Set;
  const selected = controlled ? selectedProp : ownSelection;

  const isSortable = (c) => c.sortable !== false && !!c.label;
  const primaryCol = useMemo(() => {
    const shown = columns.filter((c) => !c.mobileHide);
    return shown.find((c) => c.primary) || columns.find((c) => c.primary) || shown[0] || columns[0];
  }, [columns]);

  const labelOf = (row, i) => {
    if (getRowLabel) return getRowLabel(row, i);
    if (!primaryCol) return `Row ${i + 1}`;
    const raw = primaryCol.sortValue ? primaryCol.sortValue(row) : row[primaryCol.key];
    return plain(raw) || `Row ${i + 1}`;
  };

  const toggleSort = (c) => {
    setSort((prev) => {
      if (prev.key !== c.key) return { key: c.key, dir: 'asc' };
      if (prev.dir === 'asc') return { key: c.key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  };

  const sortedRows = useMemo(() => {
    const indexed = rows.map((row, i) => ({ row, i }));
    if (!sort.key) return indexed;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return indexed;
    const valueOf = col.sortValue ? col.sortValue : (row) => row[col.key];
    const flip = sort.dir === 'desc' ? -1 : 1;
    return [...indexed].sort((a, b) => {
      const av = valueOf(a.row);
      const bv = valueOf(b.row);
      if (isEmptyValue(av) || isEmptyValue(bv)) return compareValues(av, bv);
      return flip * compareValues(av, bv);
    });
  }, [rows, columns, sort]);

  const visibleKeys = sortedRows.map(({ row, i }) => rowKeyOf(row, i));
  const visibleKeySig = visibleKeys.join('\u0001');
  const allSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selected.has(k));
  const someSelected = visibleKeys.some((k) => selected.has(k));

  /* Selection plumbing */
  const emit = (next) => {
    if (!controlled) setOwnSelection(next);
    if (onSelectionChange) onSelectionChange(next);
  };
  const toggleRow = (rk, row) => {
    if (onSelectionChange || !controlled) {
      const next = new Set(selected);
      if (next.has(rk)) next.delete(rk); else next.add(rk);
      emit(next);
    }
    if (onToggleRow) onToggleRow(rk, row);
  };
  const toggleAll = () => {
    if (onSelectionChange || !controlled) {
      const next = new Set(selected);
      if (allSelected) visibleKeys.forEach((k) => next.delete(k));
      else visibleKeys.forEach((k) => next.add(k));
      emit(next);
    }
    if (onToggleAll) onToggleAll(visibleKeys, allSelected);
  };

  // Prune selection when rows change (filter, refetch, delete).
  const emitRef = useRef(emit);
  emitRef.current = emit;
  useEffect(() => {
    if (!selectable || selected.size === 0) return;
    const present = new Set(visibleKeySig ? visibleKeySig.split('\u0001') : []);
    const pruned = new Set([...selected].filter((k) => present.has(String(k))));
    if (sameSet(pruned, selected)) return;
    if (controlled && !onSelectionChange && onToggleRow) {
      // Legacy toggle API: switch each vanished key off.
      [...selected].filter((k) => !pruned.has(k)).forEach((k) => onToggleRow(k, undefined));
    } else {
      emitRef.current(pruned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKeySig, selectable]);

  const cell = (c, row, i) => (c.render ? c.render(row, i) : row[c.key]);
  const colSpan = columns.length + (selectable ? 1 : 0);

  const openRow = onRowClick ? (row, i) => onRowClick(row, i) : null;
  const rowKeyDown = (row, i) => (e) => {
    if (e.target !== e.currentTarget) return;           // keys inside a cell control stay there
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRow(row, i); }
  };

  /* Non-data states */
  const stateBlock = () => {
    if (error) return <ErrorState thing={errorThing} error={error} onRetry={onRetry} compact />;
    if (loading && rows.length === 0) return <Loading variant="table" rows={5} label={`Loading ${errorThing}`} />;
    if (rows.length === 0) return emptyState || <div className="cw-table-empty">{emptyText}</div>;
    return null;
  };
  const state = stateBlock();

  /* ── Mobile: one card per row ───────────────────────────────────────────── */
  if (narrow) {
    const shown = columns.filter((c) => !c.mobileHide);
    const primary = shown.find((c) => c.primary) || shown[0];
    const rest = shown.filter((c) => c !== primary);
    const sortables = columns.filter(isSortable);

    return (
      <div className="cw-cards" aria-busy={loading || undefined}>
        {!state && (sortables.length > 1 || selectable) && (
          <div className="cw-cards-sort">
            {sortables.length > 1 && (
              <>
                <label htmlFor="cw-cards-sortby">Sort</label>
                <select id="cw-cards-sortby" value={sort.key || ''} onChange={(e) => setSort({ key: e.target.value || null, dir: 'asc' })}>
                  <option value="">Default order</option>
                  {sortables.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                {sort.key && (
                  <button
                    type="button"
                    onClick={() => setSort((p) => ({ ...p, dir: p.dir === 'asc' ? 'desc' : 'asc' }))}
                    aria-label={sort.dir === 'asc' ? 'Sorted ascending — switch to descending' : 'Sorted descending — switch to ascending'}
                  >
                    <i className={`fa-solid ${sort.dir === 'asc' ? 'fa-arrow-up-short-wide' : 'fa-arrow-down-wide-short'}`} aria-hidden="true" />
                  </button>
                )}
              </>
            )}
            {selectable && visibleKeys.length > 0 && (
              <button type="button" className="all" onClick={toggleAll}>
                {allSelected ? 'Clear selection' : 'Select all'}
              </button>
            )}
          </div>
        )}

        {state}

        {!state && sortedRows.map(({ row, i }) => {
          const rk = rowKeyOf(row, i);
          const isSel = selected.has(rk);
          const label = labelOf(row, i);
          return (
            <div
              key={rk}
              className={`cw-dcard${isSel ? ' sel' : ''}${openRow ? ' tappable' : ''}`}
            >
              {openRow && (
                <button type="button" className="cw-dcard-hit" onClick={() => openRow(row, i)} aria-label={`Open ${label}`} />
              )}
              <div className="cw-dcard-head">
                {selectable && (
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggleRow(rk, row)}
                    aria-label={`Select ${label}`}
                  />
                )}
                <div className="cw-dcard-title">{primary ? cell(primary, row, i) : null}</div>
              </div>
              {rest.length > 0 && (
                <dl className="cw-dcard-fields">
                  {rest.map((c) => {
                    const v = cell(c, row, i);
                    if (v === null || v === undefined || v === '') return null;
                    return (
                      <React.Fragment key={c.key}>
                        <dt>{c.label}</dt>
                        <dd>{v}</dd>
                      </React.Fragment>
                    );
                  })}
                </dl>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Desktop: the table ─────────────────────────────────────────────────── */
  return (
    <div className="cw-scroll cw-table-wrap" aria-busy={loading || undefined}>
      {openRow && <span id={openHintId} className="cw-sr-only">Press Enter to open</span>}
      <table className="cw-table" style={{ minWidth }}>
        {caption && <caption className="cw-sr-only">{caption}</caption>}
        <thead>
          <tr>
            {selectable && (
              <th scope="col" className="cw-th-check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={toggleAll}
                  disabled={!!state}
                  aria-label={allSelected ? 'Clear selection' : 'Select all rows'}
                />
              </th>
            )}
            {columns.map((c) => {
              const sortable = isSortable(c);
              const active = sortable && sort.key === c.key;
              const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : (sortable ? 'none' : undefined);
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={ariaSort}
                  style={{ textAlign: c.align || 'left', width: c.width, ...(c.thStyle || {}) }}
                >
                  {sortable ? (
                    <button
                      type="button"
                      className={`cw-th-sort${active ? ' active' : ''}`}
                      onClick={() => toggleSort(c)}
                      style={c.align === 'right' ? { flexDirection: 'row-reverse' } : undefined}
                    >
                      <span>{c.label}</span>
                      <i
                        className={active ? `fa-solid ${sort.dir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}` : 'fa-solid fa-sort'}
                        aria-hidden="true"
                      />
                    </button>
                  ) : c.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {state && (
            <tr className="cw-table-state">
              <td colSpan={colSpan}>{state}</td>
            </tr>
          )}
          {!state && sortedRows.map(({ row, i }) => {
            const rk = rowKeyOf(row, i);
            const isSel = selected.has(rk);
            const label = labelOf(row, i);
            return (
              <tr
                key={rk}
                className={`cw-row${openRow ? ' openable' : ''}${isSel ? ' sel' : ''}`}
                tabIndex={openRow ? 0 : undefined}
                // A description, not a label: the cells stay the row's accessible text.
                aria-describedby={openRow ? openHintId : undefined}
                onClick={openRow ? (e) => { if (!fromInteractive(e)) openRow(row, i); } : undefined}
                onKeyDown={openRow ? rowKeyDown(row, i) : undefined}
              >
                {selectable && (
                  <td className="cw-td-check">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleRow(rk, row)}
                      aria-label={`Select ${label}`}
                    />
                  </td>
                )}
                {columns.map((c) => (
                  <td key={c.key} style={{ textAlign: c.align || 'left', ...(c.tdStyle || {}) }}>
                    {cell(c, row, i)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
